import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import axios from "axios";
import { ReactSortable } from "react-sortablejs";
import useAlertMessage from "../../../hooks/useAlertMessage";
import { API_URL, EMAIL_REGEX } from "../../../constants/Globals";
import { BarsIcon } from "../../../icons";
import { addStepToSequence } from "../../../services/CollectionsSvc";

export default function NewFormModal({
  show,
  onClose,
  selNetworkId,
  match,
  sequenceId,
  orderNumber,
}) {
  const { params } = match;
  const [loading, setLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [formName, setFormName] = useState("");
  const [formTitle, setFormTitle] = useState("");
  const [formFields, setFormFields] = useState([]);
  const [formDescription, setFormDescription] = useState("");
  const [formButtonText, setFormButtonText] = useState("");
  const [formRedirectLink, setFormRedirectLink] = useState("");
  const [formImage, setFormImage] = useState(null);
  const [notificationEmails, setNotificationEmails] = useState("");
  const [setAlertMessage, getAlertMessage] = useAlertMessage();
  const fieldTypes = ["name", "email", "phone", "custom"];

  useEffect(() => {
    if (params.id === "new") {
      setFormName("");
      setFormTitle("");
      setFormDescription("");
      setFormButtonText("");
      setFormRedirectLink("");
      setFormFields([]);
      setDataFetched(false);
      setFormImage(null);
      setNotificationEmails("");
    } else if (!dataFetched) {
      setDataFetched(true);

      axios.get(`${API_URL}/forms/${params.id}`).then(({ data }) => {
        const {
          name,
          title,
          description,
          buttonText,
          redirectLink,
          fields,
          image,
          notificationEmails,
        } = data.data;

        setFormName(name);
        setFormTitle(title);
        setFormDescription(description);
        setFormButtonText(buttonText);
        setFormRedirectLink(redirectLink);
        setNotificationEmails(notificationEmails.join(", "));
        setFormFields(fields);

        if (image) {
          setFormImage(`https://cdn.sided.co/prod/sided/forms/${image}-small`);
        }
      });
    }
  }, [params, dataFetched, show]);

  return (
    <Modal show={show} onHide={onClose} size="lg" centered keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>Add New Form</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <section>
          {getAlertMessage()}

          <div className="segment-form-body padding-s">
            <div className="segment-form-field">
              <label className="type-2">Form Name</label>
              <input
                type="text"
                value={formName}
                className="full-width"
                placeholder="Enter name here"
                onChange={({ target }) => setFormName(target.value)}
              />
            </div>

            <div className="segment-form-field">
              <label className="type-2">
                Form Title
                <span className="info-note">Shows on the embedded form</span>
              </label>
              <input
                type="text"
                value={formTitle}
                className="full-width"
                placeholder="Enter title here"
                onChange={({ target }) => setFormTitle(target.value)}
              />
            </div>

            <div className="segment-form-field">
              <label className="type-2">Form Description</label>
              <input
                type="text"
                value={formDescription}
                className="full-width"
                placeholder="Enter description here"
                onChange={({ target }) => setFormDescription(target.value)}
              />
            </div>

            <div className="segment-form-field">
              <label className="type-2">
                Image
                <span className="info-note">Optional</span>
              </label>
              <div className="segment-form-image">
                {formImage ? (
                  <figure>
                    <img src={formImage} alt="Form graphic" />
                  </figure>
                ) : null}

                <div className="segment-form-picker">
                  {formImage ? (
                    <span className="text-button">Replace Image</span>
                  ) : (
                    <span className="blue-button bordered inline">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17.5 3.125C17.8438 3.125 18.125 3.40625 18.125 3.75V16.2422L17.9297 15.9883L12.6172 9.11328C12.4414 8.88281 12.1641 8.75 11.875 8.75C11.5859 8.75 11.3125 8.88281 11.1328 9.11328L7.89062 13.3086L6.69922 11.6406C6.52344 11.3945 6.24219 11.25 5.9375 11.25C5.63281 11.25 5.35156 11.3945 5.17578 11.6445L2.05078 16.0195L1.875 16.2617V16.25V3.75C1.875 3.40625 2.15625 3.125 2.5 3.125H17.5ZM2.5 1.25C1.12109 1.25 0 2.37109 0 3.75V16.25C0 17.6289 1.12109 18.75 2.5 18.75H17.5C18.8789 18.75 20 17.6289 20 16.25V3.75C20 2.37109 18.8789 1.25 17.5 1.25H2.5ZM5.625 8.75C5.87123 8.75 6.11505 8.7015 6.34253 8.60727C6.57002 8.51305 6.77672 8.37494 6.95083 8.20083C7.12494 8.02672 7.26305 7.82002 7.35727 7.59253C7.4515 7.36505 7.5 7.12123 7.5 6.875C7.5 6.62877 7.4515 6.38495 7.35727 6.15747C7.26305 5.92998 7.12494 5.72328 6.95083 5.54917C6.77672 5.37506 6.57002 5.23695 6.34253 5.14273C6.11505 5.0485 5.87123 5 5.625 5C5.37877 5 5.13495 5.0485 4.90747 5.14273C4.67998 5.23695 4.47328 5.37506 4.29917 5.54917C4.12506 5.72328 3.98695 5.92998 3.89273 6.15747C3.7985 6.38495 3.75 6.62877 3.75 6.875C3.75 7.12123 3.7985 7.36505 3.89273 7.59253C3.98695 7.82002 4.12506 8.02672 4.29917 8.20083C4.47328 8.37494 4.67998 8.51305 4.90747 8.60727C5.13495 8.7015 5.37877 8.75 5.625 8.75Z"
                          fill="#2F80ED"
                        />
                      </svg>
                      Upload
                    </span>
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    onChange={({ target }) => {
                      if (target.value && target.files[0]) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setFormImage(reader.result);
                        };
                        reader.readAsDataURL(target.files[0]);
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="segment-form-field">
              <label className="type-2">Form Fields</label>
              <div className="inner-dropdown">
                <svg
                  className="dropdown-arrow"
                  width="8"
                  height="5"
                  viewBox="0 0 8 5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.94 0L4 3.06L7.06 0L8 0.946667L4 4.94667L0 0.946667L0.94 0Z"
                    fill="#4F4F4F"
                  />
                </svg>
                <input
                  type="text"
                  readOnly={true}
                  className="full-width"
                  placeholder="Select a form field"
                  onFocus={({ target }) =>
                    (target.parentNode.querySelector(
                      ".auto-complete"
                    ).style.display = "block")
                  }
                  onBlur={({ target }) =>
                    setTimeout(
                      () =>
                        (target.parentNode.querySelector(
                          ".auto-complete"
                        ).style.display = "none"),
                      250
                    )
                  }
                />
                <div className="auto-complete" style={{ display: "none" }}>
                  {fieldTypes.map((fieldType) => (
                    <div key={fieldType}>
                      <div className="is-capitalized">{fieldType}</div>
                      <div
                        className="text-button"
                        onClick={() =>
                          setFormFields(
                            formFields.concat([
                              {
                                fieldType,
                                label:
                                  fieldType.charAt(0).toUpperCase() +
                                  fieldType.slice(1),
                                placeholderText: `Enter ${
                                  fieldType === "custom"
                                    ? `${fieldType} placeholder text`
                                    : `your ${fieldType}`
                                }`,
                                isRequired: true,
                              },
                            ])
                          )
                        }
                      >
                        Add
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {formFields.length ? (
              <div className="summary-table">
                <div className="summary-table-row summary-table-head">
                  <div className="summary-cell summary-cell-w1 visibility-l">
                    #
                  </div>
                  <div className="summary-cell summary-cell-w3 visibility-l">
                    Field
                  </div>
                  <div className="summary-cell summary-cell-fluid visibility-l">
                    Placeholder text
                  </div>
                  <div className="summary-cell summary-cell-w2 visibility-l center-x">
                    Required
                  </div>
                  <div className="summary-cell summary-cell-w2 visibility-l"></div>
                  <div className="summary-cell summary-cell-w1 visibility-l"></div>
                </div>

                <ReactSortable list={formFields} setList={setFormFields}>
                  {formFields.map(
                    (
                      { fieldType, label, placeholderText, isRequired },
                      index
                    ) => (
                      <div className="summary-table-row" key={index}>
                        <div className="summary-cell summary-cell-w1 visibility-l">
                          {index + 1}
                        </div>
                        <div className="summary-cell summary-cell-w3">
                          {label}
                        </div>
                        <div className="summary-cell summary-cell-fluid">
                          <input
                            type="text"
                            value={placeholderText}
                            onChange={({ target }) =>
                              setFormFields(
                                [].concat(formFields).map((field, idx) => {
                                  return {
                                    ...field,
                                    placeholderText:
                                      idx === index
                                        ? target.value
                                        : field.placeholderText,
                                  };
                                })
                              )
                            }
                          />
                        </div>
                        <div className="summary-cell summary-cell-w2 center-x">
                          <input
                            type="checkbox"
                            checked={isRequired}
                            onChange={() => {}}
                            onClick={() =>
                              setFormFields(
                                [].concat(formFields).map((field, idx) => {
                                  return {
                                    ...field,
                                    isRequired:
                                      idx === index
                                        ? !isRequired
                                        : field.isRequired,
                                  };
                                })
                              )
                            }
                          />
                        </div>
                        <div className="summary-cell summary-cell-w2">
                          <div
                            className="text-button"
                            onClick={() =>
                              setFormFields(
                                []
                                  .concat(formFields)
                                  .filter((formField, idx) => idx !== index)
                              )
                            }
                          >
                            Remove
                          </div>
                        </div>
                        <div className="summary-cell summary-cell-w1 center-x">
                          <BarsIcon />
                        </div>
                      </div>
                    )
                  )}
                </ReactSortable>
              </div>
            ) : null}

            <div className="segment-form-field">
              <label className="type-2">Button Text</label>
              <input
                type="text"
                value={formButtonText}
                className="full-width"
                placeholder="Enter button text"
                onChange={({ target }) => setFormButtonText(target.value)}
              />
            </div>

            <div className="segment-form-field mb-l">
              <label className="type-2">
                Redirect Link
                <span className="info-note">Optional</span>
              </label>
              <input
                type="text"
                value={formRedirectLink}
                className="full-width"
                placeholder="Enter link to redirect user to after submission"
                onChange={({ target }) => setFormRedirectLink(target.value)}
              />
            </div>

            <div className="segment-form-field mb-l">
              <label className="type-2">Notification Emails</label>
              <input
                type="text"
                value={notificationEmails}
                className="full-width"
                onChange={({ target }) => setNotificationEmails(target.value)}
              />
            </div>

            <div className="segment-form-buttons align-right">
              <button
                className="blue-button"
                onClick={async () => {
                  const notificationMail = notificationEmails.split(",");
                  let isValidEmail = true;
                  notificationMail.forEach((email) => {
                    if (!EMAIL_REGEX.test(email) && isValidEmail) {
                      isValidEmail = false;
                    }
                  });
                  if (!isValidEmail) {
                    setAlertMessage("Please enter valid notificaiton email.");
                    return;
                  }
                  setLoading(true);

                  let image = undefined;
                  const imageInput = document.querySelector(
                    ".segment-form-image input[type=file]"
                  );

                  if (imageInput && imageInput.value) {
                    const formData = new FormData();
                    formData.append(
                      "backgroundImage",
                      imageInput.files[0],
                      imageInput.files[0].name
                    );

                    const imageData = await axios.post(
                      `${API_URL}/forms/imageUpload?type=FORMS&clientId=${selNetworkId}`,
                      formData
                    );

                    if (imageData && imageData.data && imageData.data.data) {
                      image = imageData.data.data;
                    }
                  }

                  let formId = null;
                  await axios({
                    method: params.id === "new" ? "POST" : "PUT",
                    url: `${API_URL}/forms${params.id !== "new" ? `/${params.id}` : ""}`,
                    data: {
                      clientId: parseInt(selNetworkId),
                      name: formName,
                      title: formTitle,
                      description: formDescription,
                      buttonText: formButtonText,
                      redirectLink: formRedirectLink,
                      notificationEmails: notificationMail
                        .map((item) => item.trim())
                        .filter((item) => Boolean(item)),
                      formFields,
                      image,
                    },
                  })
                    .then(({ data }) => {
                      formId = data.data.id;
                      setLoading(false);
                      setAlertMessage(data.message);
                      setTimeout(() => {
                        if (
                          !window.location.pathname.includes(
                            `/sequences/${sequenceId}`
                          )
                        ) {
                          window.location.replace(
                            window.location.href.replace(
                              "/sequences",
                              `/sequences/${sequenceId}`
                            )
                          );
                        }
                      }, 1000);
                    })
                    .catch(({ response }) => {
                      const { data } = response;
                      const { message } = data;

                      setLoading(false);

                      setAlertMessage(
                        typeof message === "string"
                          ? message
                          : message.map((error) => error.message).join("\n")
                      );
                    });

                  if (formId && sequenceId) {
                    try {
                      const payload = {
                        sequenceId: sequenceId,
                        order: orderNumber + 1,
                        isBranch: false,
                        partType: "form",
                        partId: formId,
                      };

                      const response = await addStepToSequence(payload);
                      const nextId = response.data.data;
                      onClose(nextId);
                    } catch (error) {
                      console.error("Failed to add step to sequence:", error);
                      alert(
                        "Failed to add step to sequence. Please try again."
                      );
                    }
                  }
                }}
                disabled={loading}
              >
                Save Form
              </button>
            </div>
          </div>
        </section>
      </Modal.Body>
    </Modal>
  );
}
